import {
  calculateDateFromInterval,
  calculateHoursDifference,
  calculateMinutesDifference,
  DayIntervalUnit,
  formatDateToMMDDYYYY,
  getLocalTime,
  getWallTimeInstant,
  MS_TO_UNITS,
  RoutineWorkflow,
  SCHEDULE_DEFAULTS,
  useAssetRoutines,
  useUpdateMutation,
  useWorkspaceRoutine,
  WorkspaceAsset,
} from '@gripp/shared-logic';
import * as Haptics from 'expo-haptics';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Platform, Pressable, Text, View } from 'react-native';
import { Button } from '../../crop-circle/Button/Button';
import { Icon } from '../../crop-circle/icons';
import { tokens } from '../../crop-circle/tokens';
import { fontWeights } from '../../crop-circle/tokens/typography';
import { getSkippedCompletionDateOrDays } from './helpers';
import { AlertTriggerConfigKeys } from './types';
import { Colors } from '../../../themes/colors';
import { themedStyleSheet } from '../../../themes/themedStyleSheet';

export const excludedConfigKeys = [
  'workspaceRoutineId',
  'routineType',
  'startDate',
  'onIntervalStart',
  'triggerCounterRoutineId',
  'resetDate',
  'dayInterval',
  // Schedule metadata, not an alternative trigger condition — must stay
  // excluded so editAlertForm's positional alertKeys[0]/[1] derivation and
  // this component's "OR" trigger-label list don't treat it as one.
  'atTime',
  'timeZone',
];

export type AlertSummaryProps = {
  workspaceAsset?: WorkspaceAsset | null;
  routineId: string;
  workflow: RoutineWorkflow;
  onAlertSelected: (alert: RoutineWorkflow) => void;
};

export const AlertSummary = ({
  workspaceAsset,
  routineId,
  workflow,
  onAlertSelected,
}: AlertSummaryProps) => {
  const isIos = Platform.OS === 'ios';
  const { trigger, uom, count } = workflow;
  const { t } = useTranslation();

  // Safe defaults so hooks are always called (Rules of Hooks)
  const config = trigger?.config ?? {};
  const triggerCounterId = config.triggerCounterRoutineId || '';

  const { mutateAsync: updateWorkflow, isPending } = useUpdateMutation({
    query: UPDATE_WORKFLOW,
    modelName: 'workflow',
  });

  const triggerWorkspaceRoutine = useWorkspaceRoutine(triggerCounterId);
  const triggerUOM = triggerWorkspaceRoutine?.config?.uom;
  const mainWorkspaceRoutine = useWorkspaceRoutine(
    config.workspaceRoutineId ?? ''
  );
  const triggerConfigKeys = Object.keys(config);

  // Get completion count using custom resolver
  const { assetRoutinesCount } = useAssetRoutines(
    workspaceAsset?.id,
    triggerCounterId || config.workspaceRoutineId,
    (config as any)?.resetDate
  );

  const filteredKeys = triggerConfigKeys.filter((name) => {
    return !excludedConfigKeys.includes(name);
  });

  const routineState = workspaceAsset?.routineState;

  const counterOrRoutineId = triggerCounterId || config.workspaceRoutineId;

  const latestRoutineState = useMemo(() => {
    if (!routineState || !counterOrRoutineId) return undefined;

    const result = routineState.find(
      (state) => state.workspaceRoutineId === counterOrRoutineId
    );

    return result;
  }, [routineState, counterOrRoutineId]);

  const lastRoutineDate = useMemo(() => {
    return latestRoutineState?.state.updatedAt;
  }, [latestRoutineState]);

  // Extract trigger state to access lastTriggered and lastCount
  const workflowTriggerState = useMemo(() => {
    if (!workspaceAsset?.workflowState || !workflow.id) return undefined;

    return workspaceAsset.workflowState.find(
      (state) => state.workflowId === workflow.id
    )?.triggerState as any;
  }, [workspaceAsset?.workflowState, workflow.id]);

  const lastAlertDate = useMemo(() => {
    return workflowTriggerState?.lastTriggered;
  }, [workflowTriggerState]);

  const lastAlertCount = useMemo(() => {
    return workflowTriggerState?.lastCount;
  }, [workflowTriggerState]);

  const getDifferenceInDays = (dateStr: string) => {
    const alertDate = new Date(dateStr);
    const now = new Date();

    const differenceTime = alertDate.getTime() - now.getTime();
    const differenceDays =
      differenceTime < 0
        ? 0
        : Math.ceil(differenceTime / MS_TO_UNITS[DayIntervalUnit.Days]);

    return differenceDays;
  };

  // Renders the alert's effective fire time — the saved atTime/timeZone when a
  // user picked one, otherwise the schedule default the backend fires at — as
  // the device-local clock time for the "Next Alert" line. Sub-day
  // (MINUTES/HOURS) alerts don't use this: they render their computed next
  // occurrence instant instead.
  const formatEffectiveAlertTime = (fallback: {
    atTime: string;
    timeZone: string;
  }) => {
    const effective =
      config.atTime && config.timeZone
        ? { atTime: config.atTime, timeZone: config.timeZone }
        : fallback;
    return getLocalTime(
      getWallTimeInstant(effective.atTime, effective.timeZone)
    );
  };

  // Helper to get reference date for days since completed calculations
  const getReferenceDateForDaysSinceCompleted = () => {
    let referenceDate = (config as any)?.resetDate
      ? new Date((config as any).resetDate)
      : undefined;

    if (!referenceDate && config.startDate) {
      referenceDate = new Date(config.startDate);
    }

    if (!referenceDate) {
      referenceDate = lastRoutineDate;
    }

    if (!referenceDate) {
      referenceDate = workflow.createdAt
        ? new Date(workflow.createdAt)
        : new Date();
    }

    return referenceDate;
  };

  // Simple label for entity-level alerts (no asset state)
  const getSimpleTriggerLabel = (key: AlertTriggerConfigKeys) => {
    switch (key) {
      case AlertTriggerConfigKeys.dayIntervalUnit:
        const interval = config.dayInterval;
        const unit = config.dayIntervalUnit;
        const unitLabel =
          unit === DayIntervalUnit.Days
            ? t('alerts.days')
            : unit === DayIntervalUnit.Hours
            ? t('alerts.hours')
            : t('alerts.minutes');
        return (
          <Text style={[tokens.typography.label.medium, styles.subHeaderLabel]}>
            {t('alerts.every')} {interval} {unitLabel}
          </Text>
        );

      case AlertTriggerConfigKeys.onDate:
        return (
          <Text style={[tokens.typography.label.medium, styles.subHeaderLabel]}>
            {t('alerts.on')} {formatDateToMMDDYYYY(config.onDate)}
          </Text>
        );

      case AlertTriggerConfigKeys.onInterval:
        return (
          <Text style={[tokens.typography.label.medium, styles.subHeaderLabel]}>
            {t('alerts.every')} {config.onInterval} {t('alerts.completions')}
          </Text>
        );

      case AlertTriggerConfigKeys.onCompletionCount:
        return (
          <Text style={[tokens.typography.label.medium, styles.subHeaderLabel]}>
            {t('alerts.every')} {config.onCompletionCount}{' '}
            {t('alerts.completions')}
          </Text>
        );

      case AlertTriggerConfigKeys.daysSinceCompleted: {
        const daysSinceValue = Number(config.daysSinceCompleted) || 0;
        const referenceDate = getReferenceDateForDaysSinceCompleted();
        const elapsed = getSkippedCompletionDateOrDays(
          referenceDate,
          daysSinceValue,
          false
        );

        return (
          <Text style={[tokens.typography.label.medium, styles.subHeaderLabel]}>
            {elapsed} {t('alerts.days')} {t('alerts.elapsed')}
          </Text>
        );
      }

      default:
        return null;
    }
  };

  const getTriggerLabel = (key: AlertTriggerConfigKeys) => {
    // For completion-based alerts without asset context, show simplified labels
    const completionBasedTriggers = [
      AlertTriggerConfigKeys.onInterval,
      AlertTriggerConfigKeys.onCompletionCount,
      AlertTriggerConfigKeys.daysSinceCompleted,
    ];

    if (!workspaceAsset && completionBasedTriggers.includes(key)) {
      return getSimpleTriggerLabel(key);
    }

    let alertValue = config[key];
    let nextAlert = t('alerts.nextAlert');
    let currentState = t('alerts.currently');
    let daysValue;
    let daysLabel: string;
    let difference: number;
    const intervalStartValue = config.onIntervalStart;
    let nextInterval;

    const AlertLabels = () => (
      <>
        <Text style={[tokens.typography.label.medium, styles.subHeaderLabel]}>
          {nextAlert}
        </Text>
        <Text style={[tokens.typography.label.medium, styles.subHeaderLabel]}>
          {currentState}
        </Text>
        <Text style={[tokens.typography.label.medium, styles.subHeaderLabel]}>
          {t('alerts.lastAlert')}
          {lastAlertDate
            ? `${formatDateToMMDDYYYY(new Date(lastAlertDate))} ${t(
                'alerts.at'
              )} ${getLocalTime(new Date(lastAlertDate).toISOString())}`
            : t('alerts.lastAlertNA')}
        </Text>
      </>
    );

    switch (key) {
      case AlertTriggerConfigKeys.dayIntervalUnit: {
        if (
          !config.dayInterval ||
          !config.dayIntervalUnit ||
          !config.startDate
        ) {
          return;
        }

        const unit = config.dayIntervalUnit;
        const intervalValue = config.dayInterval;
        let baselineStart = (config as any)?.resetDate || config.startDate;

        // Check if resetDate is earlier than startDate, use startDate if so
        if ((config as any)?.resetDate && config.startDate) {
          const resetDateTime = new Date((config as any).resetDate);
          const startDateTime = new Date(config.startDate);

          if (resetDateTime < startDateTime) {
            baselineStart = config.startDate;
          }
        }

        const targetDate = calculateDateFromInterval(
          intervalValue,
          unit,
          baselineStart
        );
        const now = new Date();

        switch (unit) {
          case DayIntervalUnit.Minutes: {
            daysValue = calculateMinutesDifference(targetDate, now);
            daysLabel =
              daysValue > 1 ? t('alerts.minutes') : t('alerts.minute');
            break;
          }
          case DayIntervalUnit.Hours: {
            daysValue = calculateHoursDifference(targetDate, now);
            daysLabel = daysValue > 1 ? t('alerts.hours') : t('alerts.hour');
            break;
          }
          default: {
            daysValue = getDifferenceInDays(targetDate.toISOString());
            daysLabel = daysValue > 1 ? t('alerts.days') : t('alerts.day');
          }
        }

        // Calculate the newDate for display. Sub-day units have no wall-clock
        // config — their next occurrence IS the computed target instant — while
        // day-level units fire at the picked atTime or the schedule default.
        nextAlert += formatDateToMMDDYYYY(targetDate.toISOString());
        nextAlert += ` ${t('alerts.at')} ${
          unit === DayIntervalUnit.Minutes || unit === DayIntervalUnit.Hours
            ? getLocalTime(targetDate.toISOString())
            : formatEffectiveAlertTime(SCHEDULE_DEFAULTS.recurringAlert)
        }`;

        currentState += `${daysValue} ${daysLabel} ${t('alerts.togo')}`;

        return <AlertLabels />;
      }
      case AlertTriggerConfigKeys.onDate:
        if (typeof alertValue === 'string') {
          daysValue = getDifferenceInDays(alertValue);
        } else if (alertValue instanceof Date) {
          daysValue = getDifferenceInDays(alertValue.toISOString());
        } else {
          daysValue = 0;
        }

        daysLabel =
          daysValue > 1 || daysValue < 1 ? t('alerts.days') : t('alerts.day');

        nextAlert += formatDateToMMDDYYYY(
          alertValue instanceof Date ? alertValue : (alertValue as string)
        );
        nextAlert += ` ${t('alerts.at')} ${formatEffectiveAlertTime(
          SCHEDULE_DEFAULTS.oneTime
        )}`;
        currentState += `${daysValue} ${daysLabel} ${t('alerts.togo')}`;

        return <AlertLabels />;
      case AlertTriggerConfigKeys.onInterval: {
        const latestRoutineStateCount =
          Number(latestRoutineState?.state?.count) || 0;
        const intervalStart = Number(intervalStartValue) || 0;
        const interval = Number(alertValue);

        const hasLastAlertCount =
          typeof lastAlertCount === 'number' && !Number.isNaN(lastAlertCount);

        if (
          hasLastAlertCount &&
          lastAlertCount > intervalStart &&
          lastAlertCount <= latestRoutineStateCount
        ) {
          // Normal: anchor next interval on where the alert actually last fired
          nextInterval =
            intervalStart +
            (Math.floor(Math.abs(lastAlertCount - intervalStart) / interval) +
              1) *
              interval;
        } else if (
          hasLastAlertCount &&
          lastAlertCount > intervalStart &&
          lastAlertCount > latestRoutineStateCount
        ) {
          // Fat-finger: lastAlertCount is above the current counter value —
          // snap to the next grid line above current count
          nextInterval =
            intervalStart +
            (Math.ceil((latestRoutineStateCount - intervalStart) / interval) ||
              1) *
              interval;
        } else {
          // No prior alert: fire at the first interval mark
          nextInterval = intervalStart + interval;
        }

        difference = Math.max(0, nextInterval - latestRoutineStateCount);
        nextAlert += `${nextInterval} ${triggerUOM}`;
        currentState += `${difference} ${triggerUOM} ${t('alerts.togo')}`;

        return <AlertLabels />;
      }
      case AlertTriggerConfigKeys.floor:
        nextAlert += `${alertValue} ${uom}`;
        currentState += `${count || '--'} ${uom}`;
        return <AlertLabels />;

      case AlertTriggerConfigKeys.ceiling:
        nextAlert += `${alertValue} ${uom}`;
        currentState += `${count || '--'} ${uom}`;
        return <AlertLabels />;

      case AlertTriggerConfigKeys.onCompletionCount: {
        const routinesCount = assetRoutinesCount?.count || 0;
        const effectiveCount = routinesCount;

        alertValue = Number(alertValue);

        difference = alertValue - (effectiveCount % alertValue);

        if (difference < 0) {
          difference = 0;
        }

        nextAlert += `${difference} ${t('alerts.completions')} ${t(
          'alerts.togo'
        )}`;
        currentState += `${effectiveCount} ${t('alerts.completions')} ${t(
          'alerts.recorded'
        )}`;
        return <AlertLabels />;
      }
      case AlertTriggerConfigKeys.daysSinceCompleted: {
        const daysSinceValue = Number(alertValue) || 0;
        const referenceDate = getReferenceDateForDaysSinceCompleted();

        nextAlert += `${getSkippedCompletionDateOrDays(
          referenceDate,
          daysSinceValue,
          true
        )}`;
        currentState += `${getSkippedCompletionDateOrDays(
          referenceDate,
          daysSinceValue,
          false
        )} ${t('alerts.days')} ${t('alerts.elapsed')}`;
        return <AlertLabels />;
      }
      default:
        return;
    }
  };

  const includeResetButton = useMemo(() => {
    const timeBasedWithReset = [AlertTriggerConfigKeys.dayIntervalUnit];
    const completionBasedWithReset = [
      AlertTriggerConfigKeys.onInterval,
      AlertTriggerConfigKeys.onCompletionCount,
      AlertTriggerConfigKeys.daysSinceCompleted,
    ];

    // Always show reset for time-based alerts
    const hasTimeBasedReset = filteredKeys.some((item) =>
      timeBasedWithReset.includes(item as AlertTriggerConfigKeys)
    );

    // Only show reset for completion-based alerts if we have a workspaceAsset
    const hasCompletionBasedReset =
      workspaceAsset &&
      filteredKeys.some((item) =>
        completionBasedWithReset.includes(item as AlertTriggerConfigKeys)
      );

    return hasTimeBasedReset || hasCompletionBasedReset;
  }, [workflow, workspaceAsset, filteredKeys]);

  const onResetPress = async () => {
    const currentTime = new Date();
    let resetDate = currentTime.toISOString();

    // Check if resetDate is earlier than startDate, use startDate but with current time
    if (config.startDate) {
      const resetDateTime = new Date(resetDate);
      const startDateTime = new Date(config.startDate);

      if (resetDateTime < startDateTime) {
        // Use startDate but with current time components (hours, minutes, seconds, ms)
        const startDateWithCurrentTime = new Date(config.startDate);

        if (
          config.dayIntervalUnit === DayIntervalUnit.Minutes ||
          config.dayIntervalUnit === DayIntervalUnit.Hours
        ) {
          startDateWithCurrentTime.setHours(
            currentTime.getHours(),
            currentTime.getMinutes(),
            currentTime.getSeconds(),
            currentTime.getMilliseconds()
          );
        }

        resetDate = startDateWithCurrentTime.toISOString();
      }
    }

    const currentCount = latestRoutineState?.state.count || 0;
    const resetConfig: any = {
      ...config,
      resetDate,
    };

    if (filteredKeys.includes(AlertTriggerConfigKeys.onInterval)) {
      resetConfig.onIntervalStart = String(currentCount);
    }

    const input = {
      id: workflow.id,
      trigger: {
        ...trigger,
        config: resetConfig,
      },
    };

    await updateWorkflow({ input: input });
  };

  const ResetButton = () => {
    return (
      <Button
        variant="rectangular"
        size="default"
        state={isPending ? 'outline' : 'active'}
        loading={isPending}
        disabled={isPending}
        leftIcon={
          <Icon
            name="Reset"
            size={16}
            color={
              isPending
                ? tokens.colors.semantic.info
                : tokens.colors.neutral[100]
            }
          />
        }
        onPress={() => {
          isIos && Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Medium);
          onResetPress();
        }}
        style={styles.resetButton}
      >
        {t('alerts.reset')}
      </Button>
    );
  };

  const AlertTriggerLabels = () => {
    return filteredKeys.map((key, index) => {
      return (
        <View key={key}>
          {getTriggerLabel(key as AlertTriggerConfigKeys)}
          {index < filteredKeys.length - 1 && (
            <Text style={styles.subHeaderLabel}>{t('alerts.OR')}</Text>
          )}
        </View>
      );
    });
  };

  const onAlertPress = (workflow: RoutineWorkflow) => {
    onAlertSelected(workflow);
  };

  // Guard: render nothing when trigger/config is missing (after all hooks)
  if (!trigger || !trigger.config) {
    return null;
  }

  return (
    <Pressable onPress={() => onAlertPress(workflow)} style={styles.container}>
      {/* Row 1: Alert icon + name + chevron (no reset button here) */}
      <View style={styles.headerRow}>
        <View style={styles.headerLeft}>
          <Icon name="Alert" size={24} color={Colors.role.textStrong} />
          <Text
            allowFontScaling={false}
            adjustsFontSizeToFit={true}
            style={styles.headerLabel}
            numberOfLines={1}
            ellipsizeMode="tail"
          >
            {workflow.name || t('alerts.alert')}
          </Text>
        </View>
        <View style={styles.chevronContainer}>
          <Icon name="Chevron Right" size={20} color={Colors.role.textStrong} />
        </View>
      </View>
      {!workspaceAsset && mainWorkspaceRoutine && (
        <Text style={styles.routineNameLabel}>{mainWorkspaceRoutine.name}</Text>
      )}
      {/* Row 2: Trigger labels (sub-headers) + reset button on same row, different from alert name */}
      <View style={styles.subHeaderRow}>
        <View style={styles.triggerLabelsContainer}>
          <AlertTriggerLabels />
        </View>
        {includeResetButton && (
          <View style={styles.resetButtonContainer}>
            <ResetButton />
          </View>
        )}
      </View>
    </Pressable>
  );
};

const styles = themedStyleSheet((role) => ({
  container: {
    minHeight: 92,
    backgroundColor: role.canvas,
    borderRadius: 2,
    borderWidth: 1,
    borderColor: role.borderSubtle,
    marginBottom: tokens.spacing.md,
    flexDirection: 'column',
    paddingVertical: tokens.spacing.xl,
    paddingHorizontal: tokens.spacing.lg,
  },
  headerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  headerLeft: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.sm,
    minWidth: 0,
  },
  chevronContainer: {
    justifyContent: 'center',
    alignItems: 'center',
    marginLeft: tokens.spacing.sm,
  },
  subHeaderRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: tokens.spacing.sm,
    gap: tokens.spacing.md,
  },
  headerLabel: {
    ...tokens.typography.body.small,
    fontWeight: fontWeights.semibold,
    color: role.textStrong,
  },
  routineNameLabel: {
    ...tokens.typography.body.small,
    fontWeight: fontWeights.semibold,
    color: role.textSecondary,
    marginTop: tokens.spacing.xs,
  },
  triggerLabelsContainer: {
    flex: 1,
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: tokens.spacing.xs,
    minWidth: 0,
  },
  subHeaderLabel: {
    color: role.textSecondary,
  },
  buttonContainer: {
    gap: tokens.spacing.md,
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'center',
  },
  resetButton: {
    minWidth: 70,
  },
  resetButtonContainer: {
    justifyContent: 'flex-end',
    alignItems: 'flex-end',
    flexShrink: 0,
  },
}));

const UPDATE_WORKFLOW = `
  mutation UpdateWorkflow($input: MutateWorkflowInput!) {
  updateWorkflow(input: $input) {
    id
  }
}
`;
